import { useState } from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { Theme } from '@app/theme';
import {
  LargeModaleWithLeftPane,
  NotificationPin,
  SquareButton,
  useSessionUnreadMessageByParticipation,
} from 'components';
import { MessagingTab, useParticipationDisplayName } from '@app/core';
import { Participation, StudyType, useIsAdmin, useSession } from '@app/api';

import { DetailsModalLeftPane } from './DetailsModalLeftPane';
import { ScreeningFormResults } from './ScreeningFormResults';
import { AppointmentDetails } from './AppointmentDetails';
import { ParticipantProfileDetails } from './ParticipantProfileDetails';
import { Direction } from '../../ParticipantMonitoring';
import { ParticipationNotes } from './ParticipationNotes';
import { MeetingRating } from './MeetingRating';
import { AutomaticValidationResults } from './AutomaticValidationResults';
import { HistoryTab } from './HistoryTab';
import { ParticipationDocuments } from './ParticipationDocuments';
import { ParticipationBillingTab } from './ParticipationBillingTab';

const useStyles = createUseStyles((theme: Theme) => ({
  mainPane: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  paneContent: {
    padding: theme.marginBase * 4,
    paddingTop: 0,
    flex: 1,
    overflow: 'auto',
    '& > *:last-child': {
      marginBottom: theme.marginBase * 4,
    },
  },
  profilePane: {
    paddingTop: 0,
  },
  screeningForm: {
    marginBottom: theme.marginBase * 4,
  },
  meetingRating: {
    marginBottom: theme.marginBase * 4,
  },
  closeIcon: {
    position: 'absolute',
    zIndex: 1,
    top: 0,
    right: -theme.height.small - theme.marginBase,
    [theme.media.tablet]: {
      right: 0,
    },
  },
  arrowIcons: {
    position: 'absolute',
    top: 0,
    left: -theme.height.small - theme.marginBase,
    [theme.media.tablet]: {
      left: 0,
    },
  },
  arrowDownIcon: {
    marginTop: theme.marginBase,
  },
  tabs: {
    padding: 2 * theme.marginBase,
    paddingBottom: 0,
    position: 'relative',
    borderBottom: {
      width: 1,
      color: theme.colors.grayBorder,
      style: 'solid',
    },
    [theme.media.tablet]: {
      marginLeft: 0,
    },
    '&.MuiTabs-root, & .MuiTabs-scrollable, & .MuiTabs-fixed': {
      overflow: 'visible',
    },
    '& .MuiTab-root': {
      paddingRight: theme.marginBase,
      paddingLeft: theme.marginBase,
    },
    '& .MuiTabs-indicator': {
      width: 4,
    },
    '& .admin-tab': {
      color: theme.colors.purpleAccessible,
    },
  },
  customTab: {
    position: 'relative',
  },
  pin: {
    top: theme.marginBase,
    right: 0,
  },
}));

interface Props {
  participation?: Participation;
  isOpen: boolean;
  onRequestClose: () => any;
  onArrowClick?: (direction: Direction) => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  sessionId: string;
  openBookingAppointmentModal?: () => any;
  reschedule?: (id: string) => any;
  cancel?: (id: string) => any;
  onRequestMoveToOtherSession?: (id: string) => any;
}

enum TabsNames {
  APPLICATION = 'APPLICATION',
  PROFILE = 'PROFILE',
  MESSAGES = 'MESSAGES',
  HISTORY = 'HISTORY',
  NOTES = 'NOTES',
  BILLING = 'BILLING',
}

export const ParticipationDetailsModal = ({
  participation,
  isOpen,
  onRequestClose,
  onArrowClick,
  canGoPrevious,
  canGoNext,
  sessionId,
  openBookingAppointmentModal,
  reschedule,
  cancel,
  onRequestMoveToOtherSession,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { data: session } = useSession(participation?.sessionId);
  const [currentTab, setTab] = useState<TabsNames>(
    (searchParams.get('tab') as TabsNames) || TabsNames.APPLICATION,
  );
  const { participant } = participation || {};
  const isAdmin = useIsAdmin();

  const handleTabChange = (_: any, tab: TabsNames) => setTab(tab);

  const unreadMessageCount = useSessionUnreadMessageByParticipation(
    participation?.sessionId,
    participation?.id,
  );

  const goToNextParticipation = () =>
    onArrowClick && onArrowClick(Direction.NEXT);
  const goToPreviousParticipation = () =>
    onArrowClick && onArrowClick(Direction.PREVIOUS);

  const participantName = useParticipationDisplayName(participation);

  return (
    <LargeModaleWithLeftPane
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      paneTitle={participant ? participantName : participation?.trackingId}
      paneContent={
        participation && <DetailsModalLeftPane participation={participation} />
      }
    >
      {participation ? (
        <>
          {onArrowClick && (
            <div className={classes.arrowIcons}>
              <SquareButton
                color="blue"
                icon="ri-arrow-up-s-line"
                onClick={goToPreviousParticipation}
                variant="muted"
                overGray
                disabled={!canGoPrevious}
              />
              <SquareButton
                color="blue"
                icon="ri-arrow-down-s-line"
                onClick={goToNextParticipation}
                variant="muted"
                className={classes.arrowDownIcon}
                overGray
                disabled={!canGoNext}
              />
            </div>
          )}
          <div className={classes.mainPane}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="Tabs"
              indicatorColor="primary"
              textColor="primary"
              className={classes.tabs}
              variant="fullWidth"
            >
              <Tab
                label={t('sessions.details.tabs.application')}
                value={TabsNames.APPLICATION}
              />
              {participation?.participantId && (
                <Tab
                  label={
                    <>
                      {unreadMessageCount > 0 && (
                        <NotificationPin className={classes.pin} />
                      )}
                      {t('sessions.details.tabs.messages')}
                    </>
                  }
                  value={TabsNames.MESSAGES}
                ></Tab>
              )}
              <Tab
                label={t('sessions.details.tabs.history')}
                value={TabsNames.HISTORY}
              />
              <Tab
                label={t('sessions.details.tabs.notes')}
                value={TabsNames.NOTES}
              />
              {isAdmin && participation?.participantId && (
                <Tab
                  label={t('sessions.details.tabs.profile')}
                  value={TabsNames.PROFILE}
                  className="admin-tab"
                />
              )}
              {isAdmin && (
                <Tab
                  label={t('sessions.details.tabs.billing')}
                  value={TabsNames.BILLING}
                  className="admin-tab"
                />
              )}
            </Tabs>
            {currentTab === TabsNames.APPLICATION && (
              <div className={classes.paneContent}>
                {session?.studyType === StudyType.MODERATED && (
                  <>
                    <MeetingRating
                      participation={participation}
                      className={classes.meetingRating}
                      reschedule={reschedule}
                    />
                    <AppointmentDetails
                      participation={participation}
                      className={classes.screeningForm}
                      reschedule={reschedule}
                      cancel={cancel}
                      key={participation.id}
                    />
                  </>
                )}
                <AutomaticValidationResults
                  participation={participation}
                  className={classes.screeningForm}
                />
                <ParticipationDocuments participationId={participation.id} />

                <ScreeningFormResults
                  participation={participation}
                  className={classes.screeningForm}
                  onDecline={goToNextParticipation}
                  onAccept={goToNextParticipation}
                  onRequestMoveToOtherSession={onRequestMoveToOtherSession}
                  onBookingModalOpening={openBookingAppointmentModal}
                />
              </div>
            )}
            {currentTab === TabsNames.PROFILE &&
              participation.participantId && (
                <div
                  className={classnames(
                    classes.paneContent,
                    classes.profilePane,
                  )}
                >
                  <ParticipantProfileDetails
                    participantId={participation.participantId}
                    sessionId={sessionId}
                  />
                </div>
              )}
            {currentTab === TabsNames.MESSAGES && (
              <MessagingTab participation={participation} />
            )}
            {currentTab === TabsNames.HISTORY && (
              <HistoryTab participation={participation} />
            )}
            {currentTab === TabsNames.NOTES && (
              <ParticipationNotes participation={participation} />
            )}
            {currentTab === TabsNames.BILLING && (
              <ParticipationBillingTab participation={participation} />
            )}
          </div>
        </>
      ) : null}
    </LargeModaleWithLeftPane>
  );
};
